import { MetricLog } from '@/types/metrics'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<MetricLog>({
  pathCategory: 'metric_logs',
})

export function useGetMetricLog({
  id,
  ...options
}: UseGetDetailOptions<MetricLog>) {
  const query = useGetDetail<MetricLog>({
    id,
    queryKey: queryKeys.metricLogs.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    metricLog: query.data,
    ...query,
  }
}

export function useCreateMetricLog(
  options: UseCreateDetailOptions<MetricLog> = {},
) {
  const mutation = useCreateDetail<MetricLog>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createMetricLog: mutation.mutate,
    createMetricLogAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateMetricLog(
  options: UseUpdateDetailOptions<MetricLog> = {},
) {
  const mutation = useUpdateDetail<MetricLog>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateMetricLog: mutation.mutate,
    updateMetricLogAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteMetricLog(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<MetricLog>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteMetricLog: mutation.mutate,
    deleteMetricLogAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetMetricLogs({
  filters,
  ...options
}: UseGetListOptions<MetricLog> = {}) {
  const query = useGetList<MetricLog>({
    filters,
    queryKey: queryKeys.metricLogs.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    metricLogs: query.allData,
    ...query,
  }
}

export function useUpdateMetricLogs(
  options: UseUpdateListOptions<MetricLog> = {},
) {
  const mutation = useUpdateList<MetricLog>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateMetricLogs: mutation.mutate,
    updateMetricLogsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteMetricLogs({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<MetricLog>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteMetricLogs: mutation.mutate,
    deleteMetricLogsAsync: mutation.mutateAsync,
    ...mutation,
  }
}
